import { useVenueState, useClosedStateMessage, useBrand } from "@touchscreen/hooks";
import { Artwork } from "@touchscreen/brand-kit";
import { ProgressBar } from "../components/ProgressBar.js";
import { Header } from "../components/Header.js";
import { artworkUrl } from "../lib/artwork.js";
import heroRhythmTop from "../assets/hero-rhythm-top.avif";
import heroRhythmBottom from "../assets/hero-rhythm-bottom.avif";
import "./AttractScreen.css";

export interface AttractScreenProps {
  onTouch: () => void;
}

/** The default view: what's on, what's next, and a clear invitation to touch. */
export function AttractScreen(props: AttractScreenProps): React.JSX.Element {
  const { ready, nowPlaying, progress, upNext, recentlyPlayed, requestsOpen, requestsClosedReason, nextTransitionAt, playerOnline } =
    useVenueState();
  // The rainbow rhythm glows below are startle.io's hero art specifically, not a brand-neutral
  // decoration — other brands get --brand-glow's flat radial wash instead (see Header.css).
  const { brandKey } = useBrand();
  const closedMessage = useClosedStateMessage(requestsClosedReason, nextTransitionAt);
  // The offline toast already covers this reason — showing the same line again in the footer
  // is redundant, so the footer falls back to blank rather than repeating it.
  const footerMessage = requestsClosedReason === "player_offline" ? null : closedMessage;

  // The whole screen is the tap target to browse — except the two song lists, which need to
  // absorb their own scroll/tap gestures without launching the catalogue underneath a scroll.
  const stopListPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <div className="attract-screen" onClick={requestsOpen ? props.onTouch : undefined}>
      {/* Ripped from startle.io's hero — same rainbow rhythm glows, corner to corner. Startle-only:
          other brands shouldn't inherit startle.io's imagery. */}
      {brandKey === "startle" ? (
        <>
          <img className="attract-screen__rhythm attract-screen__rhythm--top" src={heroRhythmTop} alt="" aria-hidden="true" />
          <img className="attract-screen__rhythm attract-screen__rhythm--bottom" src={heroRhythmBottom} alt="" aria-hidden="true" />
        </>
      ) : null}

      <Header />

      <p className="attract-screen__hint">{requestsOpen ? "Tap anywhere to choose the next track" : null}</p>

      <section className="attract-screen__now-playing">
        {nowPlaying ? (
          <>
            <Artwork className="attract-screen__artwork" src={artworkUrl(nowPlaying.sm_id)} />
            <p className="attract-screen__eyebrow">Now playing</p>
            <h1 className="attract-screen__title">{nowPlaying.title}</h1>
            <h2 className="attract-screen__artist">{nowPlaying.artist}</h2>
            {progress ? <ProgressBar ratio={progress.ratio} /> : null}
          </>
        ) : playerOnline ? (
          <p className="attract-screen__eyebrow">Nothing playing right now</p>
        ) : null}
      </section>

      {/* Gate on `ready` — before the first snapshot lands, `playerOnline` defaults to false
          and would otherwise flash this toast during initial load. */}
      {ready && !playerOnline ? (
        <p className="attract-screen__toast" role="status">
          The music system here isn't responding right now
        </p>
      ) : null}

      <section className="attract-screen__lists">
        <div className="attract-screen__list" onClick={stopListPropagation}>
          <h3>Previously played</h3>
          <ul>
            {recentlyPlayed.length === 0 ? <li className="attract-screen__empty">Nothing played yet</li> : null}
            {recentlyPlayed.map((track) => (
              <li className="attract-screen__track" key={`${track.sm_id}-${track.played_at}`}>
                <Artwork className="attract-screen__list-artwork" src={artworkUrl(track.sm_id)} />
                <span className="attract-screen__list-text">
                  <span>{track.title}</span>
                  <span className="attract-screen__list-artist">{track.artist}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="attract-screen__list" onClick={stopListPropagation}>
          <h3>Up next</h3>
          <ul>
            {upNext.length === 0 ? <li className="attract-screen__empty">Nothing queued</li> : null}
            {upNext.map((track, index) => (
              // sm_id alone isn't a safe key: the same track can appear more than once in the queue.
              <li className="attract-screen__track" key={`${track.sm_id}-${index}`}>
                <Artwork className="attract-screen__list-artwork" src={artworkUrl(track.sm_id)} />
                <span className="attract-screen__list-text">
                  <span>{track.title}</span>
                  <span className="attract-screen__list-artist">{track.artist}</span>
                </span>
                {track.is_request ? <span className="attract-screen__badge">Requested</span> : null}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="attract-screen__cta">
        <p>{!requestsOpen ? footerMessage : null}</p>
      </footer>
    </div>
  );
}
